import { ArrowUturnLeftIcon, ChevronDownIcon, PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useEffect } from "react"; // Ensure useEffect is imported
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EscalationData } from "../../api/rest/models/escalation";

interface EscalationFormProps {
  escalationData: EscalationData;
  selectedCategoryName: string;
  title: string;
  summary: string;
  onRecipientChange: (name: string) => void;
  onTitleChange: (title: string) => void;
  onSummaryChange: (summary: string) => void;
  onSend: () => void;
  onBack: () => void;
  isLoading?: boolean; // For showing a loading state on the send button
  sendStatus: "success" | "error" | null;
  onDismissStatus: () => void;
  createdQuestionId?: number;
}

export function EscalationForm({
  escalationData,
  selectedCategoryName,
  title,
  summary,
  onRecipientChange,
  onTitleChange,
  onSummaryChange,
  onSend,
  onBack,
  isLoading = false,
  sendStatus,
  onDismissStatus,
  createdQuestionId,
}: EscalationFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (sendStatus === "success" && createdQuestionId) {
      const timer = setTimeout(() => {
        navigate(`/question/${createdQuestionId}`);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [sendStatus, createdQuestionId, navigate]);

  if (sendStatus === "success") {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-green-50">
        <PaperAirplaneIcon className="h-12 w-12 text-green-500 mb-4 animate-pulse" />
        <p className="text-lg font-medium text-green-700">{t("chat.escalationForm.sendSuccessMessage")}</p>
      </div>
    );
  }

  if (sendStatus === "error") {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 bg-red-50">
        <XCircleIcon className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-lg font-medium text-red-700">{t("chat.escalationForm.sendErrorMessage")}</p>
        <button
          type="button"
          className="mt-4 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={onDismissStatus} // Allow user to dismiss error and see form again
        >
          {t("chat.escalationForm.backToForm")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-4 bg-gray-100">
      {/* Recipient Selection */}
      <div className="mb-4">
        <label htmlFor="recipient" className="block text-sm font-medium text-gray-700">
          {t("chat.escalationForm.recipient")}
        </label>
        <div className="relative mt-1">
          <select
            id="recipient"
            name="recipient"
            className="block w-full appearance-none rounded-md border border-gray-300 bg-white p-2 pr-10 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
            value={selectedCategoryName}
            onChange={(e) => onRecipientChange(e.target.value)}
            disabled={isLoading}
          >
            {escalationData.escalationOptions.map((option) => (
              <option key={option.name} value={option.name} title={option.description}>
                {option.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Title Input */}
      <div className="mb-4">
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          {t("chat.escalationForm.title")}
        </label>
        <input
          type="text"
          id="title"
          name="title"
          className="mt-1 block w-full p-2 rounded-md bg-white border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          disabled={isLoading}
        />
      </div>

      {/* Summary Text Area */}
      <div className="mb-4 flex-grow flex flex-col">
        <label htmlFor="summary" className="block text-sm font-medium text-gray-700">
          {t("chat.escalationForm.summary")}
        </label>
        <textarea
          id="summary"
          name="summary"
          rows={6}
          className="mt-1 block w-full p-2 rounded-md bg-white border border-gray-300 resize-none flex-grow focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          value={summary}
          onChange={(e) => onSummaryChange(e.target.value)}
          disabled={isLoading}
        />
        <p className="mt-1 text-xs text-gray-500">{t("chat.escalationForm.summaryEditable")}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4">
        <button
          data-headlessui-state
          type="button"
          className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={onBack}
        >
          <ArrowUturnLeftIcon className="h-5 w-5 mr-2" />
          {t("chat.escalationForm.back")}
        </button>
        <button
          type="button"
          className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-dobu-orange hover:bg-dobu-orange-focus focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dobu-orange-focus disabled:opacity-50"
          onClick={onSend}
          disabled={isLoading}
        >
          {isLoading ? t("chat.escalationForm.sending") : t("chat.escalationForm.send")}
          {!isLoading && <PaperAirplaneIcon className="h-5 w-5 ml-2" />}
        </button>
      </div>
    </div>
  );
}
